import {
  FC,
  Fragment,
  ReactElement,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { AccordionDetails, Box } from "@mui/material";
import moment from "moment";
import "moment/locale/nl";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionSummary,
  AccordionDetailsBox,
  SummaryContentBox,
  RidesTextBox
} from "./ChartsAccordion.styled";
import ReportObjectNameCell from "components/common/reports/ReportObjectNameCell";

interface ChartItem {
  date: string;
  rides: number;
  hourDurations: any[];
}

interface DataItem {
  serialNumber: string;
  name: string;
  isInactive?: boolean;
  dataByDate: ChartItem[];
}

const collapseUnmount = { unmountOnExit: true } as const;

const ISO_DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

export const chartAccordionFirstPanelId = (serialNumber: string) =>
  `chart-object:${serialNumber}`;

export const chartAccordionSecondPanelId = (
  serialNumber: string,
  date: string
) => `chart-object:${serialNumber}:bucket:${encodeURIComponent(date)}`;

/** Rides charts use ISO day keys; working-hours buckets use getDateKey labels (e.g. week ranges). */
const formatAccordionDateHeading = (date: string, momentLocale: string) => {
  if (!ISO_DAY_KEY.test(date)) {
    return date;
  }
  const parsed = moment(date).locale(momentLocale);
  return parsed.isValid() ? parsed.format("ddd, MMMM D, YYYY") : date;
};

/** Measures its own width so each chart can size independently (no shared ref). */
const MeasuredChartSlot: FC<{
  chartItem: ChartItem;
  renderGraphComponent: (chartItem: ChartItem, width: number) => ReactElement;
}> = ({ chartItem, renderGraphComponent }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const measure = () => {
      setWidth(Math.max(0, Math.floor(el.getBoundingClientRect().width)));
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <AccordionDetailsBox ref={ref}>
      {width > 0 ? renderGraphComponent(chartItem, width) : null}
    </AccordionDetailsBox>
  );
};

interface Props {
  data: DataItem[];
  expandedFirstLevel: string[];
  expandedSecondLevel: string[];
  handleFirstLevelAccordionChange: (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => void;
  handleSecondLevelAccordionChange: (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => void;
  chartID: string;
  renderGraphComponent: (chartItem: ChartItem, width: number) => ReactElement;
}

const ChartsAccordion: FC<Props> = ({
  data,
  expandedFirstLevel,
  handleFirstLevelAccordionChange,
  expandedSecondLevel,
  handleSecondLevelAccordionChange,
  chartID,
  renderGraphComponent
}) => {
  const { t, i18n } = useTranslation([
    "translation",
    "tableHeadingsTranslation"
  ]);
  const momentLocale = i18n.language?.toLowerCase().startsWith("nl")
    ? "nl"
    : "en";

  return (
    <Fragment>
      {data.map((item) => {
        const firstPanelId = chartAccordionFirstPanelId(item.serialNumber);
        return (
          <Accordion
            disableGutters
            key={firstPanelId}
            elevation={0}
            expanded={expandedFirstLevel.includes(firstPanelId)}
            onChange={handleFirstLevelAccordionChange}
            data-panel={firstPanelId}
            TransitionProps={collapseUnmount}
          >
            <AccordionSummary
              data-panel={firstPanelId}
              expandIcon={
                expandedFirstLevel.includes(firstPanelId) ? (
                  <ExpandLessIcon />
                ) : (
                  <ChevronRightIcon />
                )
              }
              aria-controls={`${firstPanelId}-content`}
              id={`${firstPanelId}-header`}
            >
              <SummaryContentBox>
                <ReportObjectNameCell
                  name={item.name}
                  isInactive={item.isInactive}
                />
              </SummaryContentBox>
            </AccordionSummary>
            <AccordionDetails>
              {item.dataByDate.map((chartItem) => {
                const secondPanelId = chartAccordionSecondPanelId(
                  item.serialNumber,
                  chartItem.date
                );
                return (
                  <Accordion
                    disableGutters
                    key={secondPanelId}
                    elevation={0}
                    expanded={expandedSecondLevel.includes(secondPanelId)}
                    onChange={handleSecondLevelAccordionChange}
                    data-panel={secondPanelId}
                    TransitionProps={collapseUnmount}
                  >
                    <AccordionSummary
                      data-panel={secondPanelId}
                      expandIcon={
                        expandedSecondLevel.includes(secondPanelId) ? (
                          <ExpandLessIcon />
                        ) : (
                          <ChevronRightIcon />
                        )
                      }
                      aria-controls={`${secondPanelId}-content`}
                      id={`${secondPanelId}-header`}
                    >
                      <SummaryContentBox>
                        <Box>
                          {formatAccordionDateHeading(
                            chartItem.date,
                            momentLocale
                          )}
                        </Box>
                        <RidesTextBox>
                          {chartItem.rides}{" "}
                          {chartItem.rides > 1
                            ? t("reports.sessions")
                            : t("reports.session")}
                        </RidesTextBox>
                      </SummaryContentBox>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box id={`${chartID}-${secondPanelId}`}>
                        <MeasuredChartSlot
                          chartItem={chartItem}
                          renderGraphComponent={renderGraphComponent}
                        />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Fragment>
  );
};

export default ChartsAccordion;
